import { authenticatedRequest } from './authApi';
import { getAuthToken } from '../utils/auth';

/**
 * synastryApi.js
 *
 * Thin API layer for Synastry endpoints.
 * - No React code
 * - No UI logic
 * - Centralized request/error normalization
 */

const QUICK_CACHE_TTL_MS = 5000;
const quickCompatibilityCache = new Map();
const quickCompatibilityInflight = new Map();
function normalizeQuickSignSlug(value) {
  const raw = String(value || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

  const map = {
    aries: 'aries',

    tauro: 'taurus',
    taurus: 'taurus',

    geminis: 'gemini',
    gemini: 'gemini',

    cancer: 'cancer',

    leo: 'leo',

    virgo: 'virgo',

    libra: 'libra',

    escorpio: 'scorpio',
    scorpio: 'scorpio',

    sagitario: 'sagittarius',
    sagittarius: 'sagittarius',

    capricornio: 'capricorn',
    capricorn: 'capricorn',

    acuario: 'aquarius',
    aquarius: 'aquarius',

    piscis: 'pisces',
    pisces: 'pisces',
  };

  return map[raw] || '';
}

function buildQuickCacheKey(signA, signB) {
  const a = normalizeQuickSignSlug(signA) || String(signA || '').trim().toLowerCase();
  const b = normalizeQuickSignSlug(signB) || String(signB || '').trim().toLowerCase();
  return [a, b].sort().join(':');
}

async function safeJson(res) {
  try {
    return await res.json();
  } catch {
    return null;
  }
}

async function request(url, options = {}) {
  const controller = new AbortController();
  const token = getAuthToken() || null;
  const timeoutId = setTimeout(() => controller.abort(), 15000);

  try {
    const res = await authenticatedRequest(url, {
      rawResponse: true,
      credentials: 'include',
      signal: controller.signal,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(options.headers || {}),
      },
      ...options,
      diagSource: 'synastryApi',
    });

    const data = await safeJson(res);

    if (!res.ok || !data?.ok) {
      const error = new Error(
        data?.error ||
        data?.detail ||
        (Array.isArray(data?.errors) && data.errors[0]?.message) ||
        'Synastry request failed'
      );

      error.status = res.status;
      error.payload = data;
      throw error;
    }

    if (data?.data === undefined) {
    }

    return data?.data ?? null;
  } finally {
    clearTimeout(timeoutId);
  }
}

function buildQuickUrl(signA, signB) {
  const a = normalizeQuickSignSlug(signA);
  const b = normalizeQuickSignSlug(signB);

  if (!a || !b) {
    throw new Error(`Signo inválido: '${!a ? signA : signB}'`);
  }

  const params = new URLSearchParams({
    signA: a,
    signB: b,
  });

  return `/api/synastry/quick?${params.toString()}`;
}

async function getQuickCompatibility(signA, signB) {
  const cacheKey = buildQuickCacheKey(signA, signB);
  const now = Date.now();

  const cached = quickCompatibilityCache.get(cacheKey);
  if (cached && cached.expiresAt > now) {
    return cached.data;
  }

  const inflight = quickCompatibilityInflight.get(cacheKey);
  if (inflight) {
    return inflight;
  }

  const promise = request(buildQuickUrl(signA, signB), {
    method: 'GET',
  })
    .then((data) => {
      quickCompatibilityCache.set(cacheKey, {
        data,
        expiresAt: Date.now() + QUICK_CACHE_TTL_MS,
      });
      return data;
    })
    .finally(() => {
      quickCompatibilityInflight.delete(cacheKey);
    });

  quickCompatibilityInflight.set(cacheKey, promise);
  return promise;
}

async function getQuickCompatibilityByUser(targetUserId) {
  return request(`/api/synastry/quick/user/${encodeURIComponent(targetUserId)}`, {
    method: 'GET',
  });
}

async function postDeepSynastry(personA, personB, presentationLanguage = 'es') {
  return request('/api/synastry/deep', {
    method: 'POST',
    body: JSON.stringify({
      personA,
      personB,
      presentationLanguage,
    }),
  });
}

async function getDiscoverSuggestions(limit = 5) {
  const params = new URLSearchParams({
    limit: String(limit),
  });

  return request(`/api/synastry/discover?${params.toString()}`, {
    method: 'GET',
  });
}

async function getBond(targetUserId) {
  return request(`/api/synastry/bond/${encodeURIComponent(targetUserId)}`, {
    method: 'GET',
  });
}

async function recordBondInteraction(targetUserId, type) {
  return request(`/api/synastry/bond/${encodeURIComponent(targetUserId)}/interact`, {
    method: 'POST',
    body: JSON.stringify({ type }),
  });
}

async function saveReading(mode, resultSnapshot) {
  return request('/api/synastry/readings', {
    method: 'POST',
    body: JSON.stringify({ mode, resultSnapshot }),
  });
}

async function getSavedReadings({ mode, page = 1, limit = 10 } = {}) {
  const params = new URLSearchParams();

  if (mode) params.set('mode', mode);
  params.set('page', String(page));
  params.set('limit', String(limit));

  return request(`/api/synastry/readings?${params.toString()}`, {
    method: 'GET',
  });
}

async function requestShareCard(mode, pairKey, style = 'default') {
  return request('/api/synastry/share-card', {
    method: 'POST',
    body: JSON.stringify({ mode, pairKey, style }),
  });
}

async function compareSavedSynastries(currentReading, compareReadingId) {
  return request('/api/saved-synastries/compare', {
    method: 'POST',
    body: JSON.stringify({
      currentReading,
      compareReadingId,
    }),
  });
}

const synastryApi = {
  getQuickCompatibility,
  getQuickCompatibilityByUser,
  postDeepSynastry,
  getDiscoverSuggestions,
  getBond,
  recordBondInteraction,
  saveReading,
  getSavedReadings,
  requestShareCard,
  compareSavedSynastries,
};

export {
  getQuickCompatibility,
  getQuickCompatibilityByUser,
  postDeepSynastry,
  getDiscoverSuggestions,
  getBond,
  recordBondInteraction,
  saveReading,
  getSavedReadings,
  requestShareCard,
  compareSavedSynastries,
};

export default synastryApi;
